import { useEffect, useRef, useState } from "hono/jsx";
import { CodeBlock } from "./CodeBlock.tsx";

// -----------------------------------------------------------------------------
// Constants & Types
// -----------------------------------------------------------------------------

interface Snapshot {
  id: number;
  t: number;
  val: number; // Value 0-100
}

interface JitterStats {
  delta: number;
  isBad: boolean;
}

// -----------------------------------------------------------------------------
// Virtual Simulation Classes
// -----------------------------------------------------------------------------

/**
 * Simulates an authoritative server running at a fixed tick rate.
 * Generates snapshots and "sends" them to clients (via callback).
 */
class VirtualServer {
  private tickRate: number;
  private intervalId: number | null = null;
  private tickCount: number = 0;
  private onSnapshot: (s: Snapshot) => void;

  constructor(tickRateHz: number = 20, onSnapshot: (s: Snapshot) => void) {
    this.tickRate = tickRateHz;
    this.onSnapshot = onSnapshot;
  }

  public setTickRate(hz: number) {
    if (this.tickRate === hz) return;
    this.tickRate = hz;
    // Restart if running to apply new rate immediately
  }

  public start() {
    if (this.intervalId !== null) return;
    this.runTick();
  }

  private runTick() {
    this.tick();

    // Schedule next tick
    const intervalMs = 1000 / this.tickRate;
    this.intervalId = setTimeout(() => {
      this.runTick();
    }, intervalMs);
  }

  public stop() {
    if (this.intervalId !== null) {
      clearTimeout(this.intervalId);
      this.intervalId = null;
    }
  }

  private tick() {
    this.tickCount++;
    const now = Date.now();

    // SINE WAVE MOVEMENT BEHAVIOR
    // Oscillate between 10 and 90 to nice safe bounds
    const frequency = 0.002; // Speed of oscillation
    const val = 50 + Math.sin(now * frequency) * 40;

    const snapshot: Snapshot = {
      id: this.tickCount,
      t: now,
      val: val,
    };

    // "Broadcast" to client
    this.onSnapshot(snapshot);
  }
}

/**
 * Simulates the Network Layer with Jitter and Latency.
 */
class VirtualNetwork {
  private sendCallback: (s: Snapshot) => void;
  public baseLatencyMs: number = 50;
  public forceLag: boolean = false;

  constructor(onReceive: (s: Snapshot) => void) {
    this.sendCallback = onReceive;
  }

  public send(snapshot: Snapshot) {
    // Jitter calc:
    let variance = 10;
    if (this.forceLag) {
      variance = 100; // High jitter
    }

    const jitter = (Math.random() * variance * 2) - variance;

    // Occasional massive spike (5% chance when lagging)
    const isSpike = this.forceLag && Math.random() > 0.95;
    const spikeDelay = isSpike ? 400 : 0;

    // Ensure latency is never below realistic minimum (e.g. 5ms)
    const totalDelay = Math.max(5, this.baseLatencyMs + jitter + spikeDelay);

    setTimeout(() => {
      this.sendCallback(snapshot);
    }, totalDelay);
  }
}

/**
 * Simulates a Client consuming network packets and interpolating.
 */
class VirtualClient {
  private buffer: Snapshot[] = [];
  private latestSnapshot: Snapshot | null = null;

  // Stats
  public interpolationAlpha: number = 0;
  public interpolationState: "smooth" | "wait" | "snap" = "smooth";

  constructor() {}

  public onPacket(snapshot: Snapshot) {
    // Insert sorted
    if (
      this.buffer.length > 0 &&
      snapshot.t < this.buffer[this.buffer.length - 1].t
    ) {
      this.buffer.push(snapshot);
      this.buffer.sort((a, b) => a.t - b.t);
    } else {
      this.buffer.push(snapshot);
    }

    // Cap buffer size
    if (this.buffer.length > 50) {
      this.buffer.shift();
    }

    this.latestSnapshot = snapshot;
  }

  public getLatestServerState(): Snapshot | null {
    return this.latestSnapshot;
  }

  public getVisualizeSnapshots(): Snapshot[] {
    return this.buffer;
  }

  public update(renderTime: number): number {
    // 2. Find Window
    let tA: Snapshot | null = null;
    let tB: Snapshot | null = null;

    for (let i = this.buffer.length - 1; i >= 0; i--) {
      const snap = this.buffer[i];
      if (snap.t <= renderTime) {
        tA = snap;
        tB = this.buffer[i + 1] || null;
        break;
      }
    }

    // 3. Interpolate
    if (tA && tB) {
      // Normal Interpolation
      const total = tB.t - tA.t;
      const elapsed = renderTime - tA.t;
      this.interpolationAlpha = total > 0 ? elapsed / total : 0;
      this.interpolationAlpha = Math.max(
        0,
        Math.min(1, this.interpolationAlpha),
      ); // Clamp

      this.interpolationState = "smooth";
      return tA.val + (tB.val - tA.val) * this.interpolationAlpha;
    } else if (tA && !tB) {
      // Buffer Underrun (End of stream)
      this.interpolationAlpha = 1;
      this.interpolationState = "snap"; // Warning state
      return tA.val;
    } else {
      // No data at all or waiting for start
      this.interpolationState = "wait";
      return 50; // Center default
    }
  }
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export const Interpolation = () => {
  // --- UI State ---
  const [tickRate, setTickRate] = useState(20);
  const [bufferMs, setBufferMs] = useState(100);
  const [simulateLag, setSimulateLag] = useState(false); // New control

  // Realtime Visual State (React State for lower freq updates)
  const [jitterStats, setJitterStats] = useState<JitterStats[]>([]);
  const [clientStatus, setClientStatus] = useState<"smooth" | "wait" | "snap">(
    "wait",
  );

  // DOM Refs (for direct manipulation)
  const containerRef = useRef<HTMLDivElement>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  const clientRef = useRef<HTMLDivElement>(null);

  // Logic Refs (Stable Instances)
  const clientRefData = useRef<VirtualClient | null>(null);
  const serverRefData = useRef<VirtualServer | null>(null);
  const networkRefData = useRef<VirtualNetwork | null>(null);

  // Stats Text Refs
  const statsAlphaRef = useRef<HTMLSpanElement>(null);
  const statsBufferRef = useRef<HTMLSpanElement>(null);

  // Buffer Ref (to bypass closure staleness in loop)
  const bufferMsRef = useRef<number>(bufferMs);
  useEffect(() => {
    bufferMsRef.current = bufferMs;
  }, [bufferMs]);

  // --- Initialization (Once) ---
  if (!clientRefData.current) {
    clientRefData.current = new VirtualClient();
  }

  // Initialize Network once
  if (!networkRefData.current) {
    networkRefData.current = new VirtualNetwork((snapshot) => {
      // Network Callback: Receive Packet
      const now = Date.now();
      clientRefData.current!.onPacket(snapshot);

      // Jitter Stats Update
      const latency = now - snapshot.t;

      setJitterStats((prev) => {
        const isBad = latency > bufferMsRef.current!;
        const newStats = [...prev, { delta: latency, isBad }];
        if (newStats.length > 40) return newStats.slice(newStats.length - 40);
        return newStats;
      });
    });
  }

  // Initialize Server once
  if (!serverRefData.current) {
    serverRefData.current = new VirtualServer(tickRate, (s) => {
      // Server Callback: Send Packet
      networkRefData.current!.send(s);
    });
  }

  // --- Parameter Sync Effect ---
  // Updates the live instances when React state changes, WITHOUT recreating them.
  useEffect(() => {
    if (serverRefData.current) {
      serverRefData.current.setTickRate(tickRate);
    }
    if (networkRefData.current) {
      networkRefData.current.forceLag = simulateLag;
    }
  }, [tickRate, simulateLag]);

  // --- Main Loop Effect ---
  useEffect(() => {
    const server = serverRefData.current!;
    const client = clientRefData.current!;

    server.start();

    // Client Render Loop
    let animationFrameId = 0;

    const update = () => {
      // Use Ref for buffer to ensure we always interpolate with latest slider value
      const currentBufferMs = bufferMsRef.current!;
      const now = Date.now();
      const renderTime = now - currentBufferMs;

      // A. Update Client Logic
      const interpolatedVal = client.update(renderTime); // 0-100
      const latestServerSnap = client.getLatestServerState();

      // B. Visuals - Client (Green Box)
      if (clientRef.current) {
        // Use PERCENTAGE positioning
        clientRef.current.style.left = `${interpolatedVal}%`;
      }

      // C. Visuals - Ghost (Red Box)
      if (ghostRef.current && latestServerSnap) {
        const val = latestServerSnap.val;
        ghostRef.current.style.left = `${val}%`;
      }

      // E. Stats Text
      if (statsAlphaRef.current) {
        statsAlphaRef.current.innerText = client.interpolationAlpha.toFixed(2);
      }
      if (statsBufferRef.current) {
        statsBufferRef.current.innerText = `${currentBufferMs}ms`;
      }

      animationFrameId = requestAnimationFrame(update);
    };

    animationFrameId = requestAnimationFrame(update);

    return () => {
      server.stop();
      cancelAnimationFrame(animationFrameId);
    };
  }, []); // Empty dependency array: Run once on mount!

  // --- Dots Render Logic (React) ---
  const [visibleSnapshots, setVisibleSnapshots] = useState<Snapshot[]>([]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (clientRefData.current) {
        const snaps = clientRefData.current.getVisualizeSnapshots();
        setVisibleSnapshots([...snaps]);
        setClientStatus(clientRefData.current.interpolationState);
      }
    }, 48); // ~20fps update for dots
    return () => clearInterval(interval);
  }, []); // Run once

  return (
    <div class="flex flex-col gap-4 w-full max-w-lg mx-auto">
      <div class="flex items-center justify-between">
        <h3 class="font-bold text-sm uppercase opacity-50">
          Interpolation Lab
        </h3>
        <div class="badge badge-ghost font-mono text-xs">
          {clientStatus.toUpperCase()}
        </div>
      </div>

      {/* SIMULATION CONTAINER */}
      <div
        ref={containerRef}
        class="relative h-24 bg-base-300 rounded-xl overflow-hidden shadow-inner border border-base-content/5"
      >
        {/* Background Grid/Center Line */}
        <div class="absolute inset-0 flex items-center justify-center opacity-10">
          <div class="w-px h-full bg-base-content"></div>
        </div>

        {/* DOTS (Snapshots) */}
        {visibleSnapshots.map((s) => (
          <div
            key={s.id}
            class="absolute top-1/2 w-1.5 h-1.5 bg-base-content/30 rounded-full"
            style={{
              // FIXED ALIGNMENT:
              // Left position = val %
              // Transform need `-50%` on both axes to center the dot on that point.
              left: `${s.val}%`,
              top: `50%`,
              transform: `translate(-50%, -50%)`,
            }}
          />
        ))}

        {/* GHOST (Server) */}
        <div
          ref={ghostRef}
          class="absolute top-1/2 w-0 h-0 will-change-transform"
          style={{ left: "50%" }}
        >
          {/* Visual Wrapper: Center it on the point */}
          <div class="absolute -translate-x-1/2 -translate-y-1/2 w-6 h-12 border-2 border-dashed border-error/40 rounded flex items-center justify-center">
            <span class="text-[8px] font-bold text-error/80 -mt-8">SVR</span>
          </div>
        </div>

        {/* CLIENT (Interpolated) */}
        <div
          ref={clientRef}
          class="absolute top-1/2 w-0 h-0 will-change-transform"
          style={{ left: "50%" }}
        >
          {/* Visual Wrapper: Center it on the point */}
          <div
            class={`absolute -translate-x-1/2 -translate-y-1/2 w-6 h-12 rounded shadow-lg flex items-center justify-center transition-colors duration-200 ${
              clientStatus === "snap"
                ? "bg-error animate-pulse"
                : clientStatus === "wait"
                ? "bg-warning"
                : "bg-success"
            }`}
          >
            <span
              class={`text-[8px] font-bold -mb-8 ${
                clientStatus === "snap"
                  ? "text-error"
                  : clientStatus === "wait"
                  ? "text-warning"
                  : "text-success"
              }`}
            >
              YOU
            </span>
          </div>
        </div>
      </div>

      {/* JITTER GRAPH */}
      <div class="w-full h-12 bg-base-200 rounded mb-1 flex items-end gap-px relative overflow-hidden px-1 pointer-events-none">
        {/* Buffer Threshold Line */}
        <div
          class="absolute w-full h-px bg-primary z-10 opacity-50"
          style={{ bottom: `${Math.min(100, (bufferMs / 500) * 100)}%` }} // 500ms max
        >
        </div>

        {jitterStats.map((stat, i) => {
          const h = Math.min(100, (stat.delta / 500) * 100);
          return (
            <div
              key={i}
              class={`flex-1 ${stat.isBad ? "bg-error" : "bg-base-content/20"}`}
              style={{ height: `${h}%` }}
            />
          );
        })}
      </div>
      <div class="flex justify-between text-[10px] opacity-40 font-mono">
        <span>Network Latency</span>
        <span>
          {jitterStats.length > 0
            ? `${jitterStats[jitterStats.length - 1].delta.toFixed(0)}ms`
            : "0ms"}
        </span>
      </div>

      {/* CONTROLS */}
      <div class="bg-base-200 p-3 rounded-lg flex flex-col gap-3">
        {/* Tick Rate */}
        <div class="flex items-center justify-between">
          <span class="text-xs font-bold uppercase opacity-70">
            Server Tick Rate
          </span>
          <div class="join">
            {[10, 20, 60].map((hz) => (
              <button
                type="button"
                key={hz}
                class={`join-item btn btn-xs ${
                  tickRate === hz ? "btn-primary" : "btn-neutral"
                }`}
                onClick={() => setTickRate(hz)}
              >
                {hz}Hz
              </button>
            ))}
          </div>
        </div>

        {/* Buffer Slider */}
        <div>
          <div class="flex justify-between mb-1">
            <span class="text-xs font-bold uppercase opacity-70">
              Interpolation Buffer
            </span>
            <span class="text-xs font-mono" ref={statsBufferRef}>
              {bufferMs}ms
            </span>
          </div>
          <input
            type="range"
            min="0"
            max="500"
            step="10"
            value={bufferMs}
            onInput={(e) =>
              setBufferMs(Number((e.currentTarget as HTMLInputElement).value))}
            class="range range-xs range-primary"
          />
          <div class="flex justify-between text-[10px] opacity-50 mt-1">
            <span>0ms</span>
            <span>250ms</span>
            <span>500ms</span>
          </div>
        </div>

        {/* Lag Toggle */}
        <div class="form-control">
          <label class="cursor-pointer flex items-center justify-between">
            <span class="text-xs font-bold uppercase opacity-70">
              Simulate Network Spikes
            </span>
            <input
              type="checkbox"
              class="toggle toggle-xs toggle-error"
              checked={simulateLag}
              onChange={(e) =>
                setSimulateLag((e.currentTarget as HTMLInputElement).checked)}
            />
          </label>
        </div>
      </div>

      {/* FOOTER STATS */}
      <div class="grid grid-cols-2 gap-2 text-xs font-mono opacity-60 mb-4">
        <div>
          ALPHA: <span ref={statsAlphaRef}>--</span>
        </div>
        <div class="text-right">BUF: {bufferMs}ms</div>
      </div>

      {/* CODE SNIPPET */}
      <div class="mt-2 opacity-90">
        <CodeBlock
          language="typescript"
          code={`
// 1. Find the two snapshots we are between
const T_render = Date.now() - BUFFER_MS;
const A = buffer.find(snap => snap.t <= T_render);
const B = buffer.find(snap => snap.t > T_render);

// 2. Calculate progress (Alpha) 0.0 to 1.0
const progress = (T_render - A.t) / (B.t - A.t);

// 3. Lerp!
const x = lerp(A.x, B.x, progress);

// Helper
function lerp(start, end, alpha) {
  return start + (end - start) * alpha;
}
            `}
        />
      </div>

      {/* EXPLANATION */}
      <details class="collapse collapse-arrow bg-base-200 overflow-hidden rounded-lg opacity-80">
        <summary class="collapse-title text-xs font-bold uppercase opacity-70">
          How it works
        </summary>
        <div class="collapse-content text-xs flex flex-col gap-3 leading-relaxed">
          <div class="pt-2">
            <div>
              <strong class="text-secondary block mb-1">
                1. Why Interpolate?
              </strong>
              Game servers tick much slower than your 144Hz monitor (often only
              20Hz). If we rendered players exactly where the server said they
              were <i>when</i>{" "}
              the packet arrived, they would teleport 20 times a second (see the
              {" "}
              <strong>Red Ghost</strong>).
            </div>

            <div class="divider my-2 opacity-10"></div>

            <div>
              <strong class="text-warning block mb-1">
                2. The Problem: Jitter
              </strong>
              Internet latency isn't constant. Some packets arrive fast, others
              get delayed ("Jitter"). If a packet is late, we run out of data to
              draw.
            </div>

            <div class="divider my-2 opacity-10"></div>

            <div>
              <strong class="text-success block mb-1">
                3. The Solution: Render in the Past
              </strong>
              We deliberately delay rendering ("Buffering") to ensure we always
              have a <strong>future snapshot</strong> to move towards.
              <ul class="list-disc pl-4 mt-1 space-y-1">
                <li>
                  <span class="text-success font-bold">
                    Green Box (Smooth)
                  </span>: We have a future snapshot. We can smoothly slide
                  between the past and future.
                </li>
                <li>
                  <span class="text-warning font-bold">Yellow (Underrun)</span>:
                  The buffer was too short! The future packet hasn't arrived
                  yet, so we have to stop or guess.
                </li>
              </ul>
            </div>

            <div class="divider my-2 opacity-10"></div>

            <div>
              <strong class="text-accent block mb-1">
                4. What is Lerp?
              </strong>
              Short for <b>L</b>inear{" "}
              <b>Int</b>erpolation. It means finding a point partway between two
              others based on a percentage (alpha).
              <div class="mt-1 opacity-70 italic text-[10px]">
                e.g. "I am 50% (alpha=0.5) of the way from point A to point B."
              </div>
            </div>

            <div class="divider my-2 opacity-10"></div>

            <div>
              <strong class="text-primary block mb-1">
                5. What is the Buffer?
              </strong>
              A **Safety Margin** for data. Just like buffering a video, we
              deliberately wait (lag) to ensure we have a queue of packets ready
              to play.
              <div class="mt-1 opacity-70 italic text-[10px]">
                It prevents stuttering when the internet hiccups, but makes the
                game feel slightly delayed.
              </div>
            </div>

            <div class="divider my-2 opacity-10"></div>

            <div>
              <strong class="text-secondary block mb-1">
                6. How do we implement this?
              </strong>
              We don't draw the *latest* packet. Instead, we keep a list of
              recent packets. We calculate a `RenderTime = Now - Buffer`. Then
              we find the two packets surrounding that time (one slightly older,
              one slightly newer) and blend (Lerp) between them.
            </div>
          </div>
        </div>
      </details>
    </div>
  );
};
